// Libs
import classNames from 'classnames/bind';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Location, useLocation, useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseInput, UnderDevelopment } from '~/components/common';
// Others
import { LoadingContext } from '~/context';
import { IBodyVerifyCode } from '~/utils/interface/verifyCode';
import { useAppDispatch } from '~/utils/redux/hooks';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { IBodyNewPassword, IFormCreateNewPassword } from '~/utils/interface/auth';
import { createNewPassword } from '~/thunks/auth/authThunk';
import { defaultValuesForm, schema } from './helper';
// Styles, images, icons
import styles from './ResetPassword.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ResetPassword = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/auth', 'common/base']);
  const loadingContext = useContext(LoadingContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { state }: Location<IBodyVerifyCode> = useLocation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormCreateNewPassword>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!state?.code) navigate(authRouteAbsolute.forgotPassword);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.code]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleSubmitCreateNewPassword = (data: IFormCreateNewPassword) => {
    loadingContext?.show();

    const payload: IBodyNewPassword = {
      newPassword: data?.newPassword,
      code: state?.code,
    };

    dispatch(createNewPassword(payload))
      .unwrap()
      .then((resp) => {
        if (!resp.data) return;

        navigate(authRouteAbsolute.login);
      })
      .catch((_error) => {})
      .finally(() => {
        loadingContext?.hide();
      });
  };

  const handleResend = () => {
    setIsShowUnderDevelopment(true);
  };
  //#endregion Handle Function

  return (
    <Box
      id='resetPasswordPage'
      className={cx('container')}
      component={'form'}
      onSubmit={handleSubmit(handleSubmitCreateNewPassword)}
    >
      <Stack direction={'column'} spacing={'5px'} alignItems={'center'}>
        <Typography className={cx('pageName')}>{t('reset_password_title')}</Typography>

        <Typography className={cx('description')}>{t('reset_password_description')}</Typography>
      </Stack>

      <Stack direction={'column'} spacing={'20px'} marginTop={'10px'}>
        <Controller
          name='newPassword'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              height={52}
              borderRadius={'6px'}
              label={t('reset_password_title_new_password')}
              placeholder={t('reset_password_title_new_password')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.newPassword?.message}
            />
          )}
        />

        <Controller
          name='passwordConfirm'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              height={52}
              borderRadius={'6px'}
              label={t('reset_password_re_enter_password')}
              placeholder={t('reset_password_re_enter_password')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.passwordConfirm?.message}
            />
          )}
        />

        <Button variant='contained' className={cx('buttonResetPassword')} type='submit' size='large'>
          {t('reset_password_button_reset_password')}
        </Button>
      </Stack>

      <Stack direction={'row'} justifyContent={'center'} gap={1}>
        <Typography component={'span'} className={cx('textSubTitle')}>
          {t('reset_password_sub_title')}
        </Typography>
        <Typography component={'span'} className={cx('buttonResend')} onClick={handleResend}>
          {t('reset_password_sub_description')}
        </Typography>
      </Stack>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleCloseUnderDevelopment} />}
    </Box>
  );
};

export default ResetPassword;
